import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Baby } from "lucide-react";
import { getDeviceId } from "@/lib/profile";
import AudioPlayer from "@/components/julie/AudioPlayer";
import VideoPlayer from "@/components/julie/VideoPlayer";
import Lightbox from "@/components/julie/Lightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

// Respire Kids : les contenus « Public enfant » de Julie, avec lecture simple.
// Ces contenus ne sont jamais mélangés aux piliers adultes (le moteur les exclut déjà).
// Règle (Julie, 21/10/2026) : quand les Instants offerts sont terminés, l'espace Kids
// est bloqué comme le reste — il nécessite de s'abonner.
export default function Kids() {
  const [contents, setContents] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const [kidsAccess, setKidsAccess] = useState(null); // null = vérification en cours

  useEffect(() => {
    axios
      .get(`${API}/contents`)
      .then(({ data }) => setContents((data.contents || []).filter((c) => c.audience === "enfant")))
      .catch(() => {});
    const deviceId = getDeviceId();
    const token = localStorage.getItem("rav-token");
    axios
      .get(`${API}/credits?device_id=${deviceId}`, token ? { headers: { Authorization: `Bearer ${token}` } } : {})
      .then(({ data }) => setKidsAccess(data.kids_access !== false))
      .catch(() => setKidsAccess(true)); // en cas de doute réseau : on ne bloque pas
  }, []);

  // Image agrandie en plein écran — la carte reste en place en dessous
  const [zoomSrc, setZoomSrc] = useState(null);

  return (
    <main className="rav-page" data-testid="kids-page">
      <motion.div
        className="rav-kids-hero"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
      >
        <img src="/logo-accueil.webp" alt="Respire Aime Vibre" className="rav-page-logo" />
        <span className="rav-kids-badge" data-testid="kids-badge">
          <Baby size={16} strokeWidth={1.8} />
          Respire Kids
        </span>
        <h1 className="rav-page-title">Les instants des petits</h1>
        <p className="rav-page-sub">Des moments tout doux à écouter ensemble.</p>
      </motion.div>

      <div className="rav-kids-list">
        {kidsAccess === false ? (
          <div className="rav-limit-card" data-testid="kids-locked" style={{ textAlign: "center" }}>
            <p style={{ fontWeight: 600, margin: 0 }}>L'espace Kids fait partie de Respire Aime Vibre +</p>
            <p className="rav-page-sub" style={{ margin: "8px 0 14px" }}>
              Vos Instants offerts sont terminés 🤎 Pour continuer à écouter les instants des petits —
              et profiter de toute l'application — choisissez votre formule, sans engagement.
            </p>
            <a className="rav-primary-button" data-testid="kids-locked-cta" href="/offres" style={{ display: "inline-block", textDecoration: "none" }}>
              Découvrir les formules
            </a>
          </div>
        ) : (
          <>
        {contents.map((c, i) => {
          // Le bon lecteur selon le fichier : audio → lecteur, image → affichage, vidéo → lecteur vidéo
          const mediaSrc = c.media_path || c.sound_path;
          const ext = (mediaSrc || "").split(".").pop().toLowerCase();
          const isAudio = c.sound_path || ["mp3", "m4a", "wav", "ogg"].includes(ext);
          const isImage = ["png", "jpg", "jpeg", "webp"].includes(ext);
          const isVideo = ["mp4", "webm", "mov"].includes(ext);
          const playing = playingId === c.id;
          return (
            <motion.article
              key={c.id}
              className="rav-kids-card"
              data-testid={`kids-card-${i}`}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 * i, ease: EASE }}
            >
              <div className="rav-kids-card-head">
                <h2 className="rav-kids-title">« {c.title} »</h2>
                <span className="rav-kids-min">{c.minutes} min</span>
              </div>
              <p className="rav-kids-desc">{c.description}</p>
              {isImage && (
                <>
                  {/* Un appui affiche l'image en grand ; un autre la referme */}
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/media/${mediaSrc}`}
                    alt={c.title}
                    className="rav-kids-media rav-session-image-zoomable"
                    data-testid={`kids-image-${i}`}
                    loading="lazy"
                    onClick={() => setZoomSrc(`${process.env.REACT_APP_BACKEND_URL}/api/media/${mediaSrc}`)}
                    role="button"
                    aria-label="Agrandir l'image"
                    title="Toucher pour agrandir"
                  />
                  <Lightbox src={zoomSrc} alt={c.title} onClose={() => setZoomSrc(null)} testId={`kids-lightbox-${i}`} />
                </>
              )}
              {isVideo && (
                <VideoPlayer
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/media/${mediaSrc}`}
                  playing={playing}
                  onToggle={() => setPlayingId(playing ? null : c.id)}
                  testId={`kids-video-${i}`}
                />
              )}
              {!isImage && !isVideo && isAudio && (
                <AudioPlayer
                  src={`${process.env.REACT_APP_BACKEND_URL}/api/media/${mediaSrc}`}
                  playing={playing}
                  onToggle={() => setPlayingId(playing ? null : c.id)}
                  testId={`kids-player-${i}`}
                />
              )}
            </motion.article>
          );
        })}
        {contents.length === 0 && (
          <p className="rav-page-sub" data-testid="kids-empty">
            Les histoires des petits arrivent bientôt.
          </p>
        )}
          </>
        )}
      </div>
    </main>
  );
}
